import { Checkbox, ProviderConsumer } from '@fluentui/react-northstar';
import { usePersistedSort } from '../shared/components/table/usePersistedSort';
import { ListPreference, listPreferenceKey } from '../shared/components/table/listPreferences';
import { Communication } from '@fluentui/react-teams';
import { DetailsList, DetailsListLayoutMode, IColumn, Selection, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { markSortableColumns } from '../shared/components/table/columnDrag';
import { MarqueeSelection } from "@fluentui/react/lib/MarqueeSelection";
import { PersonType, UserType } from '@microsoft/mgt-components';
import { PeoplePicker } from '@microsoft/mgt-react';
import { partition } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { appState } from '../AppState';
import { searchUsers } from '../shared/cache/PlatformUserCache';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { Toolbar } from '../shared/components/Toolbar';
import { platformService } from '../shared/services/platform.service';
import { ADMINISTRATORS, DEFAULT_API_STATUS, DONE, ERROR, GETTING_ALL_LICENSE, LICENSE_DISTRIBUTION, LOADING, toastDefault, USER_LICENSE_HEADERS } from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { EnumSortOrderType } from '../tikit/toolbar/CustomViewContextProvider';
import { NameUserAvatar } from './NameUserAvatar';


interface ILicenseDistributionProperties {
  routeName: string;
  licenseName: string;
  updateLicensesDetail: () => void;
}

interface ILicenseData {
  isLicensed: boolean;
  errorMessage: string;
  isOverage: boolean;
  isAdmin: boolean;
}

interface ISeatCount {
  licensedSeatCount: number;
  seatsUsedCount: number;
}

export const LicenseDistribution = ({ routeName, licenseName, updateLicensesDetail }: ILicenseDistributionProperties) => {
  const api = new platformService();
  const currentState = appState();

  const [selection, setSelection] = useState<Selection | undefined>();
  const [columns, setColumns] = useState([] as IColumn[]);
  const [searchTerm, setSearchTerm] = useState('');

  const [data, setData] = useState([]);
  const [licenseData, setLicenseData] = useState<ILicenseData & ISeatCount>();
  const [product, setProduct] = useState<any>({});
  // Kept apart for the user page and Billing, the two places this list appears.
  const [sortOrder, setSortOrder] = usePersistedSort(
    listPreferenceKey(ListPreference.LicenseDistributionSortOrder, localStorage.getItem('tid'), routeName === 'users' ? 'user' : 'billing'),
    { orderBy: 'IsUserLicensed', orderType: EnumSortOrderType.asc }
  );

  const [apiStatus, setApiStatus] = useState<APIStatus>(DEFAULT_API_STATUS as APIStatus);
  const { t } = useTranslation();

  const setPicker = (count: number) => {
    const picker: any = document.querySelector('mgt-people-picker');
    if ((!picker && !picker?.shadowRoot) && count <= 20)
      setTimeout(() => { setPicker(count++); }, 500);

    if (picker?.shadowRoot) {
      setTimeout(() => {
        const pickerSelector = picker.shadowRoot;
        const pickerInput = pickerSelector.querySelector('#people-picker-input');
        if (pickerInput)
          pickerInput.addEventListener('input', (e: React.SyntheticEvent<HTMLElement>) => { setSearchTerm((e.target as HTMLInputElement).value); });
      }, 500);
    }
  }

  const sortData = (ev, column: IColumn) => setSortOrder({ orderBy: column.key, orderType: column.key === sortOrder.orderBy && sortOrder.orderType === EnumSortOrderType.asc ? EnumSortOrderType.desc : EnumSortOrderType.asc });
  const orderData = (a: any, b: any, by: string, dir: EnumSortOrderType): number => {
    let result = (by === 'IsUserLicensed') ? b[by] - a[by] : (a[by] ?? "").localeCompare(b[by] ?? "");
    return (dir === EnumSortOrderType.desc) ? result * -1 : result;
  }
  const filterData = (data: any[]) => {
    let filteredData = data;

    const term = searchTerm.toLocaleLowerCase();
    filteredData = filteredData.filter(data => (data.FullName.toLocaleLowerCase().includes(term) ?? false));

    if (sortOrder.orderBy !== "")
      filteredData = filteredData.sort((a, b) => orderData(a, b, sortOrder.orderBy, sortOrder.orderType));

    return filteredData;
  }
  const userColumns = (): IColumn[] => {
    return [
      {
        key: 'FullName', name: toTitleCase(USER_LICENSE_HEADERS[0]), minWidth: 250, isMultiline: false, onRender: item => (<NameUserAvatar user={item} />),
        isSorted: sortOrder.orderBy === 'FullName' ? true : null, isSortedDescending: sortOrder.orderBy === 'FullName' && sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: sortData, isResizable: false, isCollapsible: false
      },
      {
        key: 'IsUserLicensed', name: toTitleCase(USER_LICENSE_HEADERS[1]), minWidth: 100, maxWidth: 100, isMultiline: false, onRender: item => (<Checkbox checked={item.IsUserLicensed} disabled={item.IsDisabled} onChange={(e, props) => licenseChanged(item.Id, props)} />),
        isSorted: sortOrder.orderBy === 'IsUserLicensed' ? true : null, isSortedDescending: sortOrder.orderBy === 'IsUserLicensed' && sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: sortData, isResizable: false, isCollapsible: false, isMenuOpen: true
      }];
  };
  const fetchData = useCallback(async () => {
    setApiStatus({ ...apiStatus, msg: GETTING_ALL_LICENSE });
    try {
      const result = await Promise.all([
        api.getUsers("?$orderby=FullName"),
        api.getUserLicenses(),
        api.getTenantLicenses(),
        api.getLicensedAnalyst()
      ]);

      const users = result[0].data.value
      const licenses = result[1].data.value;
      const product = result[2].data.value[0];

      const licenseInfo: ILicenseData & ISeatCount = {
        ...result[3].data,
        isAdmin: currentState.userRoles.roles.some(r => r === ADMINISTRATORS),
        licensedSeatCount: product.SeatCount,
        seatsUsedCount: product.UsedCount
      }

      setLicenseData(licenseInfo);
      setProduct(product);

      let userData = users.map((user: any) => {
        const isUserLicensed = licenses.some(x => x.PlatformUserId === user.Id);
        const isDisabled = (product.SeatCount <= product.UsedCount) && !isUserLicensed;
        return {
          ...user,
          IsUserLicensed: isUserLicensed,
          IsUserLicensedPreserved: isUserLicensed,
          IsDisabled: isDisabled
        }
      });

      setData(userData);
      setPicker(0);
      setApiStatus({ ...apiStatus, status: DONE });
    } catch (err) {
      setApiStatus({ ...apiStatus, status: ERROR, errCode: err['response']?.status });
    }
  }, []);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    const _selection: Selection = new Selection({ onSelectionChanged: () => { } });
    setSelection(_selection);

    setColumns(markSortableColumns(userColumns(), () => true));
  }, [data, sortOrder, searchTerm]);

  const getNavigation = (): Breadcrumbs => ({
    breadcrumbs: [
      { title: licenseName, link: "/settings/billing" },
      { title: t('billing.license.available', { available: Math.max(0, licenseData.licensedSeatCount - licenseData.seatsUsedCount), seatCount: product.SeatCount }), link: "/settings/billing" }
    ]
  });
  const isSaveDisabled = (): boolean => data.every(x => x.IsUserLicensedPreserved == x.IsUserLicensed) || (licenseData.seatsUsedCount > licenseData.licensedSeatCount);

  const licenseChanged = (id: number, props: any) => {
    setData(prevState => {
      const item = prevState.find(x => x.Id == id);

      // Update the item in question
      item.IsUserLicensed = props.checked;

      // Disable/Enable Checkboxes as needed for all items
      if (props.checked && product.SeatCount == licenseData.seatsUsedCount + 1) prevState.forEach(x => x.IsDisabled = !x.IsUserLicensed);
      if (!props.checked && licenseData.seatsUsedCount - 1 < product.SeatCount) prevState.forEach(x => x.IsDisabled = false);

      return [...prevState];
    });
    setLicenseData(prev => ({
      ...prev,
      seatsUsedCount: prev.seatsUsedCount + (props.checked ? 1 : -1)
    }));
  };

  const saveData = async () => {
    setApiStatus({ status: LOADING, msg: t('billing.license.saving'), errCode: 0 });
    try {
      const [adds, removes] = partition(data.filter(x => x.IsUserLicensedPreserved != x.IsUserLicensed), x => x.IsUserLicensed);
      await api.updateUsersLicenses({ AddToUsers: JSON.stringify(adds), RemoveFromUsers: JSON.stringify(removes) });
      await api.resetClaims();
      toast.success(t('billing.license.update-success'), toastDefault);

      setApiStatus({ ...apiStatus, status: DONE });
      setData(prev => prev.map(x => ({ ...x, IsUserLicensedPreserved: x.IsUserLicensed })));
      updateLicensesDetail();
    } catch (err) {
      console.error(err);
      setApiStatus({ ...apiStatus, status: ERROR, errCode: err['response']?.status });
    }
  };
  const searchHandler = async (e: any) => {
    setPicker(0);
    if (e.detail.length === 0) {
      setSearchTerm('');
      return;
    }

    const { displayName, givenName, surname, userPrincipalName, scoredEmailAddresses, id } = e.detail[0];

    let user = data.find(x => x.UserName == userPrincipalName || x.AadObjectId == id);
    if (!user) {
      user = await searchUsers({ Email: scoredEmailAddresses?.[0]?.Address ?? userPrincipalName, GivenName: givenName, FamilyName: surname, UserName: userPrincipalName, AadObjectId: id } as PlatformUser)
      setData(prev => [...prev, {
        Id: user.Id,
        FullName: displayName,
        GivenName: givenName,
        FamilyName: surname,
        Email: user.Email,
        AadObjectId: id,
        IsUserLicensed: false,
        IsUserLicensedPreserved: false,
        IsDisabled: product.SeatCount <= licenseData.seatsUsedCount
      }]);
    }
    setSearchTerm(displayName);
  };

  return (
    <>
      <Helmet><title>{t('users.tabs.licenses')} - Tikit</title></Helmet>
      {apiStatus.status === LOADING && (<LoadingScreen message={apiStatus.msg} />)}
      {apiStatus.status === ERROR && (<Communication {...{ fields: { title: apiStatus.errCode === 401 ? t('common.unauthorized-access') : t('common.something-went-wrong'), desc: t('common.please-contact-your-administrator') } }} />)}
      {apiStatus.status === DONE && (
        <ProviderConsumer render={globalTheme => {
          const rows = filterData(data);
          return (<>
            <Toolbar globalTheme={globalTheme} title={LICENSE_DISTRIBUTION} bottomMargin='-0.5rem' breadcrumbs={getNavigation()} saveText={t('common.buttons.save')} saveCommand={saveData} disableSave={isSaveDisabled()} overrideLicenseCheck={(licenseData.isLicensed && licenseData.isOverage) || licenseData.isAdmin} />

            <div className='flex justify-end'>
              <div className='basis-half'><PeoplePicker userType={UserType.user} type={PersonType.person} selectionMode="single" selectionChanged={searchHandler} showMax={3} /></div>
            </div>
            <div className='flex mt-2 overflow-auto' style={{ height: "calc(100vh - 180px)" }} data-is-scrollable="true">
              <div className={`min-h-32 w-full`}>
                <MarqueeSelection selection={selection}>
                  <DetailsList items={rows} compact={false} columns={columns} selectionMode={SelectionMode.none} setKey='multiple' layoutMode={DetailsListLayoutMode.justified} isHeaderVisible={true} selection={selection} selectionPreservedOnEmptyClick={true}
                    enterModalSelectionOnTouch={true} ariaLabelForSelectionColumn='Toggle selection' ariaLabelForSelectAllCheckbox='Toggle selection for all items' checkButtonAriaLabel='select row' />
                </MarqueeSelection>
              </div>
            </div>
          </>)
        }} />
      )}
    </>
  );
}